import { axiosInstance } from '.'
import { InvestorProps, Role } from '../lib/types'
import {
  INVESTORS_LIST_V1,
  INVESTOR_PROFILE_v1,
  BLOCK_UNBLOCK_ACCOUNT_V1,
  ADMIN_INVESTOR_PORTFOLIO_STATEMENTS,
  ADMIN_INVESTOR_PORTFOLIO_STATEMENT,
  PORTFOLIO_STATEMENT_DOWNLOAD,
} from './endpoints'
import { removeEmptyValueFromObject } from '../lib/utils/helpers'

export type PortfolioStatement = {
  id: string
  investorId: number
  originalFilename: string
  label: string | null
  uploadedBy: number
  createdAt: string
  fileKey?: string
}

export type PortfolioStatementsListResponse = {
  statements: PortfolioStatement[]
}

export const investorServices = {
  getInvestorProfile: async (investorId: number) => {
    try {
      const data = await axiosInstance.get(INVESTOR_PROFILE_v1(investorId))
      const profileData: InvestorProps = {
        userId: data.data.id,
        accountStatus: data.data.account_status,
        investmentLimit: data.data.investment_limit,
        citizenship: data.data.country ?? '',
        countryCode: data.data.countryCode ?? '',
        countryOfResidence: data.data.country ?? '',
        email: data.data.email ?? '',
        name: data.data.name ?? '',
        passportNumber: data.data.passportNumber ?? '',
        phoneNumber: data.data.phone ?? '',
        kycStatus: data.data.status,
        isEmailVerified: data.data.is_verified,
        role: data.data.role,
        // Unique Client ID
        investorReference: data.data.investor_reference,
        riskProfileCompleted: Boolean(data.data.risk_profile_completed),
        riskProfileAnswers: data.data.risk_profile_answers ?? null,
        metadata: data.data.metadata,
      }
      return profileData
    } catch (error: any) {
      return error.response
    }
  },

  lockUnlockAccount: async (details: { [key: string]: string | number }) => {
    try {
      const data: any = await axiosInstance.patch(BLOCK_UNBLOCK_ACCOUNT_V1, details)
      return data
    } catch (error: any) {
      return error.response
    }
  },

  // v1
  getInvestorsList: async (roles: Role, params: object) => {
    try {
      const data: any = await axiosInstance.get(INVESTORS_LIST_V1, {
        params: removeEmptyValueFromObject(params),
      })
      return {
        pagination: {
          count: data.data.count,
          limit: data.data.limit,
          offset: data.data.offset,
          total: data.data.total,
        },
        data: data.data.investors,
        status: 200,
      }
    } catch (error: any) {
      return error.response
    }
  },

  listPortfolioStatements: async (investorId: string | number) => {
    try {
      const response = await axiosInstance.get<PortfolioStatementsListResponse>(
        ADMIN_INVESTOR_PORTFOLIO_STATEMENTS(investorId),
      )
      return response
    } catch (error: any) {
      return error.response
    }
  },

  uploadPortfolioStatement: async (investorId: string | number, file: File, label?: string) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (label && label.trim()) {
        formData.append('label', label.trim())
      }
      const response = await axiosInstance.post<PortfolioStatement>(
        ADMIN_INVESTOR_PORTFOLIO_STATEMENTS(investorId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response
    } catch (error: any) {
      return error.response
    }
  },

  deletePortfolioStatement: async (investorId: string | number, statementId: string) => {
    try {
      const response = await axiosInstance.delete<{ id: string }>(
        ADMIN_INVESTOR_PORTFOLIO_STATEMENT(investorId, statementId),
      )
      return response
    } catch (error: any) {
      return error.response
    }
  },

  downloadPortfolioStatement: async (statementId: string) => {
    try {
      const response = await axiosInstance.get<{ url: string }>(
        PORTFOLIO_STATEMENT_DOWNLOAD(statementId),
      )
      return response
    } catch (error: any) {
      return error.response
    }
  },
}
